import { useDateNow, useReduxState } from 'hooks';
import { scrollToElement } from 'lib/common';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { FlagIcon, Link, ProgressBar, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';

import type { Chapter } from 'shared-types';
import type { Editor, PageState } from 'types';

type ChapterAttr = Pick<Chapter, 'id' | 'slug' | 'title' | 'titleLocales'>;
type PreviewChapter = ChapterAttr & { number: number };

interface Props {
  activeLanguageVersion: Editor['editorActiveLanguageVersion'];
  chapter: PageState['chapter'] | PreviewChapter;
  currentHeadings?: { id: number; level: 'H2' | 'H3'; text: string }[];
  isActive: boolean;
  languageVersionTranslationProgress: number | undefined;
  previewType: Editor['editorPreviewType'];
  previewVersion: Editor['editorPreviewVersion'];
}

const EditorChaptersChapter = ({
  activeLanguageVersion,
  chapter,
  currentHeadings = [],
  isActive,
  languageVersionTranslationProgress,
  previewType,
  previewVersion
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const handleActiveClick = (ev: React.MouseEvent) => ev.preventDefault();

  const handleSubheadingClick = (
    ev: React.SyntheticEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    const blockId = parseInt(ev.currentTarget.getAttribute('data-block-id'));
    const element = document.querySelector(
      `.block[data-block-id="${blockId}"]`
    ) as HTMLElement;
    scrollToElement({ element, offset: 200 });
  };

  const languageOptions = getAllLanguageOptionsByLangCode();

  return (
    <div
      className={`chapter ${
        previewType === 'course'
          ? 'in-preview'
          : previewType === 'chapter'
          ? 'disabled'
          : ''
      }`}
    >
      <Link
        href={{
          pathname: '/admin/courses/[courseSlug]/edit/[chapterSlug]',
          query:
            previewType === 'course'
              ? {
                  courseSlug: course.slug,
                  chapterSlug: chapter.slug,
                  coursePreviewVersion: previewVersion + '',
                  ...(activeLanguageVersion
                    ? { languageVersion: activeLanguageVersion }
                    : {})
                }
              : {
                  courseSlug: course.slug,
                  chapterSlug: chapter.slug,
                  ...(activeLanguageVersion
                    ? { languageVersion: activeLanguageVersion }
                    : {})
                }
        }}
        style={{ textDecoration: 'none' }}
      >
        <div
          className={`link focus-box-shadow ${isActive ? 'active' : ''}`}
          {...(isActive ? { onClick: handleActiveClick } : {})}
        >
          <div className="chapter-title-wrapper">
            <div className="chapter-number">{chapter.number}</div>

            <div className="chapter-title">
              {chapter.titleLocales?.[activeLanguageVersion] || chapter.title}
              {!(previewType === 'course') && 'isDraft' in chapter && (
                <div className="chapter-status">
                  {isActive
                    ? messages.editEditing
                    : chapter.isDraft
                    ? messages.editSavedAsDraft
                    : messages.editSaved}
                </div>
              )}

              {activeLanguageVersion &&
              !previewType &&
              languageVersionTranslationProgress !== undefined ? (
                <div className="translation-progress">
                  <div className="translation-progress-label">
                    <FlagIcon lang={activeLanguageVersion} size="small" />
                    {languageOptions[activeLanguageVersion]?.capitalizedName}
                  </div>

                  <Tooltip
                    childrenWrapInDiv={false}
                    content={messages.courseLanguageVersionsTranslationProgress}
                  >
                    <div className="translation-progress-value">
                      <ProgressBar
                        color={
                          languageVersionTranslationProgress < 100
                            ? theme.warningStandaloneColor
                            : theme.successStandaloneColor
                        }
                        horizontal={true}
                        label={languageVersionTranslationProgress + '%'}
                        labelStyle={{ fontSize: '1.2rem' }}
                        progress={languageVersionTranslationProgress / 100}
                        progressStyle={{ width: 30 }}
                      />
                    </div>
                  </Tooltip>
                </div>
              ) : null}
            </div>
          </div>

          <div
            className="chapter-published"
            {...('publishDate' in chapter &&
            chapter.publishDate &&
            Date.parse(chapter.publishDate as string) <= dateNow
              ? {
                  'aria-label': messages.commonPublished,
                  'data-tooltip': messages.commonPublished
                }
              : 'publishDate' in chapter && chapter.publishDate
              ? {
                  'aria-label': messages.publishWillPublish,
                  'data-tooltip': messages.publishWillPublish
                }
              : {
                  'aria-label': messages.commonNotPublished,
                  'data-tooltip': messages.commonNotPublished
                })}
          >
            <Icon
              fixedWidth={true}
              icon={
                'publishDate' in chapter &&
                chapter.publishDate &&
                Date.parse(chapter.publishDate as string) <= dateNow
                  ? 'check-circle'
                  : 'publishDate' in chapter && chapter.publishDate
                  ? ['far', 'clock']
                  : 'times-circle'
              }
              style={{
                color: !('publishDate' in chapter && chapter.publishDate)
                  ? theme.dangerColor
                  : chapter.isDraft
                  ? theme.warningColor
                  : theme.successStandaloneColor
              }}
            />
          </div>
        </div>
      </Link>

      {isActive && !previewType && currentHeadings.length ? (
        <div className="subheadings">
          {currentHeadings.map(({ id, level, text }) => (
            <a
              key={id}
              className={`subheading-link ${
                level === 'H3' ? 'subheading-link-h3' : ''
              }`}
              data-block-id={id}
              onClick={handleSubheadingClick}
            >
              {text}
            </a>
          ))}
        </div>
      ) : null}

      <style jsx>{`
        .chapter:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .chapter.in-preview {
          padding: 5px 0;
        }
        .chapter.in-preview .chapter-title {
          padding-top: 2px;
        }
        .chapter.disabled .link {
          pointer-events: none;
          cursor: default;
          opacity: 0.7;
        }
        .link {
          background-color: inherit;
          border: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0;
          outline: 0;
          padding: 10px 35px 10px 15px;
          position: relative;
          text-align: left;
          text-decoration: none;
          transition: color 0.3s;
          width: 100%;
        }
        .link.active {
          color: ${theme.textColor};
          cursor: default;
          font-weight: 700;
        }
        .link:hover {
          color: ${theme.textColor};
        }
        .link:focus {
          color: ${theme.textColor};
          box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
        }
        .chapter-title-wrapper {
          display: flex;
        }
        .chapter-number {
          background-color: ${theme.lightGrey};
          border-radius: 50%;
          color: inherit;
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1.3rem;
          height: 22px;
          margin-right: 10px;
          padding: 2px 0;
          text-align: center;
          width: 22px;
        }
        .chapter-title {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .chapter-status {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 400;
          margin-top: 2px;
          position: relative;
          transition: color 0.3s;
        }
        .translation-progress {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-top: 7px;
          margin-right: -15px;
          position: relative;
        }
        .translation-progress-label {
          align-items: center;
          color: ${theme.textColorLighter};
          display: flex;
          font-size: 1.2rem;
          font-weight: 400;
          gap: 5px;
        }
        .chapter-published {
          font-size: 1.2rem;
          position: absolute;
          top: 12px;
          right: 15px;
        }
        .subheadings {
          border-left: 3px solid rgba(0, 0, 0, 0.1);
          margin-bottom: 15px;
          margin-left: 25px;
          padding: 0 15px;
        }
        .subheading-link {
          background-color: inherit;
          border: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          display: block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0;
          padding: 7px 0;
          position: relative;
          text-align: left;
          text-decoration: none;
          transition: color 0.3s;
          width: 100%;
        }
        .subheading-link-h3 {
          font-size: 1.3rem;
          margin-left: 8px;
        }
        .subheading-link:active,
        .subheading-link:hover {
          color: ${theme.textColor};
        }
      `}</style>
    </div>
  );
};

export default EditorChaptersChapter;
